import React from "react";

/**
 * Catches render errors so a failure in one screen can't blank the whole app.
 *
 * The case this exists for: every screen is loaded with React.lazy(), so each
 * one is a separate file whose name contains a build hash. Deploying replaces
 * those files, and a tab that was open beforehand still holds the old names —
 * so the next navigation requests a file the server no longer has. Without a
 * boundary React unmounts everything and the user gets a white screen, which
 * only a manual refresh clears.
 *
 * A chunk failure is recoverable, so we reload once automatically. The
 * sessionStorage flag stops that becoming a reload loop when the real problem
 * is something else.
 *
 * Rendered with plain DOM elements rather than react-native-web components:
 * this is the last line of defence, so it should depend on as little as
 * possible still working.
 */

const RELOAD_FLAG = "errorBoundary.reloadedAt";
const RELOAD_COOLDOWN_MS = 30000;

function isChunkLoadError(error) {
  if (!error) return false;
  const text = `${error.name || ""} ${error.message || ""}`;
  return (
    /ChunkLoadError/i.test(text) ||
    /Loading chunk .* failed/i.test(text) ||
    /Loading CSS chunk .* failed/i.test(text) ||
    /Failed to fetch dynamically imported module/i.test(text) ||
    /Importing a module script failed/i.test(text)
  );
}

/** True when we haven't already tried this trick in the last few seconds. */
function mayAutoReload() {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_FLAG) || 0);
    return !last || Date.now() - last > RELOAD_COOLDOWN_MS;
  } catch (e) {
    return false;
  }
}

function reloadForFreshBuild() {
  try {
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch (e) {
    // Storage unavailable — we'd rather reload without the loop guard than not
    // recover at all, since the alternative is a white screen.
  }

  // The service worker caches the app shell, so a plain reload can serve the
  // same stale index.html that pointed at the missing files. Best effort only.
  const done = () => window.location.reload();

  try {
    if (typeof caches !== "undefined" && caches.keys) {
      caches
        .keys()
        .then((keys) => Promise.all(keys.map((k) => caches.delete(k))))
        .then(done)
        .catch(done);
      return;
    }
  } catch (e) {
    // fall through
  }

  done();
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, isChunkError: isChunkLoadError(error) };
  }

  componentDidMount() {
    // A lazy import that rejects outside render won't reach the boundary, so
    // watch for it separately.
    this.onRejection = (event) => {
      if (isChunkLoadError(event?.reason) && mayAutoReload()) {
        reloadForFreshBuild();
      }
    };
    window.addEventListener("unhandledrejection", this.onRejection);
  }

  componentWillUnmount() {
    window.removeEventListener("unhandledrejection", this.onRejection);
  }

  componentDidCatch(error, info) {
    console.error("[ErrorBoundary]", error, info?.componentStack);

    if (isChunkLoadError(error) && mayAutoReload()) {
      reloadForFreshBuild();
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    // A chunk error normally reloads before this paints; it only shows if the
    // reload guard has already fired, meaning reloading didn't help.
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          textAlign: "center",
          fontFamily: "Poppins, system-ui, sans-serif",
          backgroundColor: "#FAF7F2",
          color: "#1f1f1f",
        }}
      >
        <h1 style={{ fontSize: 20, fontWeight: 700, margin: "0 0 8px" }}>
          Something went wrong
        </h1>
        <p style={{ fontSize: 15, lineHeight: 1.5, margin: "0 0 20px", maxWidth: 340 }}>
          {this.state.isChunkError
            ? "The app updated while you had it open. Reloading should sort it out."
            : "That page didn't load properly. Reloading usually fixes it."}
        </p>
        <button
          type="button"
          onClick={reloadForFreshBuild}
          style={{
            backgroundColor: "#C64A21",
            color: "#FFFFFF",
            border: "none",
            borderRadius: 8,
            padding: "12px 24px",
            fontSize: 15,
            fontWeight: 600,
            fontFamily: "inherit",
            cursor: "pointer",
          }}
        >
          Reload
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
